import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../css/ResetPassword.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [hasReset, setHasReset] = useState(false);

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setMessage('Invalid reset link. Please request a new password reset.');
            setMessageType('error');
        }
    }, [token]);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (!password || !passwordConfirm) {
            setMessage('Please fill in all fields');
            setMessageType('error');
            return;
        }

        if (password !== passwordConfirm) {
            setMessage('Passwords do not match');
            setMessageType('error');
            return;
        }

        if (password.length < 8) {
            setMessage('Password must be at least 8 characters long');
            setMessageType('error');
            return;
        }

        if (hasReset) {
            setMessage('Password has already been reset');
            setMessageType('error');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/users/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    token, 
                    password, 
                    passwordConfirm 
                }),
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.message);
                setMessageType('signup-success');
                setHasReset(true);
                setPassword('');
                setPasswordConfirm('');
                
                // Redirect to login after 3 seconds

            } else {
                setMessage(data.message);
                setMessageType('error');
            }
        } catch (error) {
            setMessage('An error occurred while resetting your password');
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-content">
                    <div className="reset-password-card">
                        <h2 className="reset-password-title">Invalid Reset Link</h2>
                        <p>This password reset link is invalid or has expired.</p>
                        <button 
                            className="default-button"
                            onClick={() => navigate('/forgot-password')}
                        >
                            Request New Reset Link
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-content">
                <div className="reset-password-card">
                    <h2 className="reset-password-title">Reset Your Password</h2>
                    
                    <form onSubmit={handleResetPassword}>
                        <div className="form-group">
                            <label htmlFor="password" className='login-label'>New Password:</label>
                            <input
                                type="password"
                                id="password"
                                placeholder="Enter your new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                required
                                disabled={hasReset}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="passwordConfirm" className='login-label'>Confirm New Password:</label>
                            <input
                                type="password"
                                id="passwordConfirm"
                                placeholder="Confirm your new password"
                                value={passwordConfirm}
                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                className="input-field"
                                required
                                disabled={hasReset}
                            />
                        </div>
                        
                        {!hasReset && (
                            <button 
                                type="submit" 
                                className="default-button submit-button"
                                disabled={loading}
                            >
                                {loading ? <div className='loader-small'/> : 'Reset Password'}
                            </button>
                        )}
                    </form>

                    {message && (
                        <div className={`${messageType}`}>
                            {message}
                            {messageType === 'signup-success' && (
                                <div>
                                    You will be redirected to the login page in a few seconds...
                                </div>
                            )}
                        </div>
                    )}

                        <a href="/" className='link switch'>
                            Back to Login
                        </a>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
